import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Link,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { CloseRounded, OpenInNewRounded, SummarizeRounded } from '@mui/icons-material';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import AndieGenericToolRender from './AndieGenericToolRender';

type CompetitorInsight = { name?: string; angle?: string; exampleUrl?: string };
type ResearchSource = {
    title?: string;
    url?: string;
    publisher?: string;
    publishedAt?: string;
    keyTakeaway?: string;
};
type Brief = {
    topic?: string;
    language?: string;
    summary?: string;
    keyFindings?: string[];
    trends?: string[];
    suggestedAngles?: string[];
    competitorInsights?: CompetitorInsight[];
    sources?: ResearchSource[];
    generatedAt?: string;
};

type ActionResponse = { researchBriefVariableUuid?: string };
type ActionArgs = Brief;

export type AndieSaveResearchBriefProps = ActionRenderAi<ActionResponse, ActionArgs>;

const domainOf = (url?: string): string => {
    if (!url) return '';
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url.replace(/^https?:\/\//, '').split('/')[0];
    }
};

const list = <T,>(value: T[] | undefined): T[] => (Array.isArray(value) ? value : []);

const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Stack gap={0.75}>
        <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ letterSpacing: 0.2 }}>
            {label}
        </Typography>
        {children}
    </Stack>
);

const Bullets = ({ items }: { items: string[] }) => (
    <Stack gap={0.5}>
        {items.map((item, index) => (
            <Stack key={index} direction="row" gap={0.75} sx={{ minWidth: 0 }}>
                <Box
                    sx={{
                        mt: '6px',
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: 'andie.main',
                        flexShrink: 0,
                    }}
                />
                <Typography variant="caption" color="text.secondary">
                    {item}
                </Typography>
            </Stack>
        ))}
    </Stack>
);

const Chips = ({ items }: { items: string[] }) => (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {items.map((item, index) => (
            <Chip
                key={index}
                label={item}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: 11, '& .MuiChip-label': { px: 1 } }}
            />
        ))}
    </Stack>
);

const ResearchBriefBody = ({ brief }: { brief: Brief }) => {
    const { t } = useTranslation();
    const keyFindings = list(brief.keyFindings).filter(Boolean);
    const trends = list(brief.trends).filter(Boolean);
    const angles = list(brief.suggestedAngles).filter(Boolean);
    const competitors = list(brief.competitorInsights).filter((c) => c && (c.name || c.angle));
    const sources = list(brief.sources).filter((s) => s && (s.url || s.title));

    const sections: React.ReactNode[] = [];
    if (brief.summary) {
        sections.push(
            <Typography key="summary" variant="caption" color="text.secondary">
                {brief.summary}
            </Typography>,
        );
    }
    if (keyFindings.length) {
        sections.push(
            <Section key="findings" label={t('andie.tool.research-brief.key-findings')}>
                <Bullets items={keyFindings} />
            </Section>,
        );
    }
    if (trends.length) {
        sections.push(
            <Section key="trends" label={t('andie.tool.research-brief.trends')}>
                <Chips items={trends} />
            </Section>,
        );
    }
    if (angles.length) {
        sections.push(
            <Section key="angles" label={t('andie.tool.research-brief.angles')}>
                <Chips items={angles} />
            </Section>,
        );
    }
    if (competitors.length) {
        sections.push(
            <Section key="competitors" label={t('andie.tool.research-brief.competitors')}>
                <Stack gap={0.75}>
                    {competitors.map((competitor, index) => (
                        <Stack
                            key={index}
                            direction="row"
                            alignItems="baseline"
                            flexWrap="wrap"
                            gap={0.5}
                            sx={{ minWidth: 0 }}
                        >
                            <Typography variant="caption" fontWeight={600} color="text.primary">
                                {competitor.name}
                            </Typography>
                            {competitor.angle && (
                                <Typography variant="caption" color="text.secondary">
                                    {competitor.name ? '— ' : ''}
                                    {competitor.angle}
                                </Typography>
                            )}
                            {competitor.exampleUrl && (
                                <Link
                                    href={competitor.exampleUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'inline-flex', color: 'andie.main' }}
                                >
                                    <OpenInNewRounded sx={{ fontSize: 12 }} />
                                </Link>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Section>,
        );
    }
    if (sources.length) {
        sections.push(
            <Section key="sources" label={t('andie.tool.research-brief.sources')}>
                <Stack gap={1}>
                    {sources.map((source, index) => {
                        const meta = [source.publisher, source.publishedAt].filter(Boolean).join(' · ');
                        return (
                            <Stack key={index} gap={0.25} sx={{ minWidth: 0 }}>
                                <Link
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        minWidth: 0,
                                        color: 'andie.main',
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        fontWeight={600}
                                        noWrap
                                        sx={{ color: 'inherit' }}
                                    >
                                        {source.title || domainOf(source.url)}
                                    </Typography>
                                    <OpenInNewRounded sx={{ fontSize: 12, flexShrink: 0 }} />
                                </Link>
                                {meta && (
                                    <Typography variant="caption" color="text.disabled" noWrap>
                                        {meta}
                                    </Typography>
                                )}
                                {source.keyTakeaway && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {source.keyTakeaway}
                                    </Typography>
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
            </Section>,
        );
    }

    return (
        <Stack gap={1.5} divider={<Divider flexItem />} sx={{ pr: 0.5 }}>
            {sections}
        </Stack>
    );
};

// Stable module-level component (NOT an inline render prop): owns its own dialog state so
// AndieGenericToolRender updates this subtree instead of remounting it (and its Dialog/portal +
// framer-motion animation) on every streamed re-render. Renders only once complete.
const ResearchBriefDetails: React.FC<ActionRenderAi<ActionResponse, ActionArgs>> = ({ status, args }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);

    if (status !== ActionResponseStatusAi.COMPLETE) return null;
    const brief: Brief = args ?? {};
    const hasContent =
        !!brief.summary ||
        list(brief.keyFindings).length > 0 ||
        list(brief.trends).length > 0 ||
        list(brief.suggestedAngles).length > 0 ||
        list(brief.competitorInsights).length > 0 ||
        list(brief.sources).length > 0;
    if (!hasContent) return null;

    return (
        <>
            <Box
                sx={{
                    maxHeight: '42vh',
                    overflow: 'hidden',
                    position: 'relative',
                    py: 0.5,
                    '&:after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: 96,
                        width: '100%',
                        pointerEvents: 'none',
                        background: `linear-gradient(to top, ${theme.palette.background.paper}, transparent)`,
                    },
                }}
            >
                <ResearchBriefBody brief={brief} />
            </Box>
            <Stack direction="row" justifyContent="center" sx={{ pt: 0.5 }}>
                <Button variant="text" size="small" onClick={() => setOpen(true)}>
                    {t('str.action.see-more')}
                </Button>
            </Stack>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullScreen={fullScreen}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Stack sx={{ minWidth: 0 }}>
                            <Typography variant="h6">
                                {t('andie.tool.save-research-brief.title')}
                            </Typography>
                            {brief.topic && (
                                <Typography variant="body2" color="text.secondary">
                                    {brief.topic}
                                </Typography>
                            )}
                        </Stack>
                        <IconButton onClick={() => setOpen(false)} size="small">
                            <CloseRounded />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <ResearchBriefBody brief={brief} />
                </DialogContent>
            </Dialog>
        </>
    );
};

const AndieSaveResearchBrief = memo((props: AndieSaveResearchBriefProps) => {
    const { t } = useTranslation();

    return (
        <AndieGenericToolRender<ActionResponse, ActionArgs>
            title={() => t('andie.tool.save-research-brief.title')}
            icon={<SummarizeRounded fontSize="small" />}
            loadingMessages={() => [t('andie.tool.save-research-brief.loading')]}
            completeMessage={({ args }) =>
                args?.topic?.trim() || t('andie.tool.save-research-brief.complete')
            }
            additionalComponent={ResearchBriefDetails}
            {...props}
        />
    );
});

AndieSaveResearchBrief.displayName = 'AndieSaveResearchBrief';

export default AndieSaveResearchBrief;
